import {
  HeatPlanExcelTable,
  parseHeatPlanExcelFile as readHeatPlanExcelTable,
} from './heat-plan-graph-excel.util';

export interface HeatPlanGraphRowMeta {
  ship_name?: string;
  ship_imo?: string | number;
  voyage_no?: string | number;
  voyage_month?: string;
  hp_np?: string | number;
  hp_number?: string | number;
  voyage_id?: string | number;
  client?: string;
  load_port?: string;
  discharge_port?: string;
  [key: string]: unknown;
}

export interface HeatPlanInfoField {
  key: string;
  label: string;
  value: string;
  icon: string;
}

export interface HeatPlanTankSeries {
  label: string;
  data: number[];
  color: string;
}

export interface HeatPlanGraphKpis {
  avgLoadTemp?: number;
  avgVoyMin?: number;
  avgVoyMax?: number;
  maxHeatingTemp?: number;
  minHeatingTemp?: number;
}

export interface HeatPlanGraphViewModel {
  vesselName: string;
  vesselImo: string;
  heatPlanNumber?: string;
  loadPort?: string;
  dischargePort?: string;
  heatingPeriod?: string;
  cargoTitle?: string;
  labels: string[];
  /** Short labels for chart x-axis, e.g. "21 May". */
  chartLabels: string[];
  tableColumns: string[];
  rowData: Record<string, unknown>[];
  tankSeries: HeatPlanTankSeries[];
  loadTempLine: number[];
  voyRangeMinLine: number[];
  voyRangeMaxLine: number[];
  voyRangeLabels: string[];
  hasLoadTemp: boolean;
  hasVoyRange: boolean;
  kpis: HeatPlanGraphKpis;
  infoFields: HeatPlanInfoField[];
}

export interface HeatPlanComparisonRow {
  parameter: string;
  originalDisplay: string;
  uploadedDisplay: string;
  difference: number | null;
  trend: 'up' | 'down' | 'flat';
}

const LAST_N_DAYS = 7;

const TANK_PORT_COLORS: Record<string, string> = {
  '1-PORT': '#38bdf8',
  '2-PORT': '#22d3ee',
  '3-PORT': '#2dd4bf',
  '4-PORT': '#818cf8',
  '5-PORT': '#60a5fa',
  '6-PORT': '#7dd3fc',
  '7-PORT': '#5eead4',
  '8-PORT': '#a5b4fc',
};

const TANK_RE = /(\d+-PORT|\d+-STBD|SLOP-PORT|SLOP-STBD)/i;
const DEFAULT_BAR_TANK_ORDER = [
  '1-PORT',
  '2-PORT',
  '3-PORT',
  '4-PORT',
  '5-PORT',
  '6-PORT',
  '7-PORT',
  '8-PORT',
];

function discoverBarTankOrder(
  columns: string[],
  rowData: Record<string, unknown>[],
): string[] {
  const ports = columns
    .filter((col) => /^\d+-PORT/i.test(col) && isTankTempColumn(col, rowData))
    .map((col) => {
      const m = col.match(TANK_RE);
      return m ? m[1].toUpperCase() : '';
    })
    .filter(Boolean);
  const unique = [...new Set(ports)];
  unique.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  return unique.length ? unique : DEFAULT_BAR_TANK_ORDER.slice(0, 6);
}

const HIDDEN_TABLE_COL_RE =
  /latitude|longitude|grad|theory|steam|temp_drop|water temp/i;

function toNumber(value: unknown): number | null {
  const s = String(value ?? '').trim();
  if (!s || s === '-' || /^x$/i.test(s) || /^on$/i.test(s)) {
    return null;
  }
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function isCargoTemp(n: number): boolean {
  return n >= 5 && n <= 120;
}

/** Typical heated cargo daily temps (excludes date fragments like 12, 5). */
function isHeatingDailyTemp(n: number): boolean {
  return n >= 25 && n <= 120;
}

function parseDateCell(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' && value > 30000 && value < 70000) {
    return (value - 25569) * 86400000;
  }
  const s = String(value).trim();
  const m = s.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
  if (m) {
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  }
  const ts = Date.parse(s);
  return Number.isFinite(ts) ? ts : null;
}

/** Reject broken PDF-parse payloads cached before navigation. */
export function isValidHeatPlanApiPayload(
  api: Record<string, unknown>,
  row: HeatPlanGraphRowMeta = {},
): boolean {
  try {
    const view = parseHeatPlanApiToView(api, row);
    const days = view.labels.length;
    const maxT = view.kpis.maxHeatingTemp ?? 0;
    return days >= 3 && maxT >= 25;
  } catch {
    return false;
  }
}

function formatDateLabel(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatChartDayLabel(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function buildChartLabels(indexes: number[]): string[] {
  return indexes.map((ts) => formatChartDayLabel(ts));
}

function dedupeRowsByDate(
  indexes: number[],
  rows: unknown[][],
): { indexes: number[]; rows: unknown[][] } {
  const byDay = new Map<number, { ts: number; row: unknown[] }>();

  indexes.forEach((ts, i) => {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    const key = d.getTime();
    byDay.set(key, { ts, row: rows[i] });
  });

  const sorted = [...byDay.values()].sort((a, b) => a.ts - b.ts);
  return {
    indexes: sorted.map((s) => s.ts),
    rows: sorted.map((s) => s.row),
  };
}

function extractObject(val: unknown): Record<string, unknown> {
  if (!val) {
    return {};
  }
  if (typeof val === 'object' && !Array.isArray(val)) {
    return val as Record<string, unknown>;
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

function avg(nums: number[]): number {
  if (!nums.length) {
    return 0;
  }
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function readPortField(api: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = api[k];
    if (v !== null && v !== undefined && String(v).trim()) {
      return String(v).trim();
    }
  }
  return undefined;
}

function isTankTempColumn(col: string, rowData: Record<string, unknown>[]): boolean {
  if (!TANK_RE.test(col)) {
    return false;
  }
  const lower = col.toLowerCase();
  if (/heat$/i.test(lower) || /\bheat\b/i.test(lower) && !/\(°c\)/i.test(col)) {
    return false;
  }
  if (/air temp|sea temp|water|wind|boiler|consp|latitude|longitude|weighted|total load|percentage/i.test(lower)) {
    return false;
  }
  const temps = rowData
    .map((r) => toNumber(r[col]))
    .filter((n): n is number => n !== null);
  if (!temps.length) {
    return false;
  }
  const heating = temps.filter((n) => isHeatingDailyTemp(n));
  return heating.length >= Math.min(2, temps.length);
}

function pickTableColumns(
  columns: string[],
  rowData: Record<string, unknown>[],
): string[] {
  return columns.filter((col) => {
    const lower = col.toLowerCase();
    if (HIDDEN_TABLE_COL_RE.test(lower)) {
      return false;
    }
    if (TANK_RE.test(col)) {
      return isTankTempColumn(col, rowData);
    }
    return /weighted|air temp|sea temp|wind|boiler|circulation|total load|boiler h/i.test(
      lower,
    );
  });
}

function buildInfoFields(view: Partial<HeatPlanGraphViewModel>): HeatPlanInfoField[] {
  const fields: HeatPlanInfoField[] = [];
  if (view.heatPlanNumber) {
    fields.push({
      key: 'hp',
      label: 'Heat Plan Number',
      value: view.heatPlanNumber,
      icon: 'description',
    });
  }
  if (view.loadPort) {
    fields.push({
      key: 'load',
      label: 'Load Port',
      value: view.loadPort,
      icon: 'location_on',
    });
  }
  if (view.dischargePort) {
    fields.push({
      key: 'disch',
      label: 'Discharge Port',
      value: view.dischargePort,
      icon: 'pin_drop',
    });
  }
  if (view.heatingPeriod) {
    fields.push({
      key: 'period',
      label: 'Heating Plan Period',
      value: view.heatingPeriod,
      icon: 'calendar_today',
    });
  }
  return fields;
}

function limitToLastNDays<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) {
    return arr;
  }
  return arr.slice(arr.length - n);
}

function sliceViewToLastDays(
  labels: string[],
  rowData: Record<string, unknown>[],
  indexes: number[],
  tankSeries: HeatPlanTankSeries[],
  loadTempLine: number[],
  voyRangeMinLine: number[],
  voyRangeMaxLine: number[],
  voyRangeLabels: string[],
  n: number,
) {
  const start = Math.max(0, labels.length - n);
  return {
    labels: labels.slice(start),
    rowData: rowData.slice(start),
    indexes: indexes.slice(start),
    tankSeries: tankSeries.map((t) => ({
      ...t,
      data: t.data.slice(start),
    })),
    loadTempLine: loadTempLine.slice(start),
    voyRangeMinLine: voyRangeMinLine.slice(start),
    voyRangeMaxLine: voyRangeMaxLine.slice(start),
    voyRangeLabels: voyRangeLabels.slice(start),
  };
}

/** Same structured data used for Heat Plan PDF download. */
export function parseHeatPlanApiToView(
  api: Record<string, unknown>,
  row: HeatPlanGraphRowMeta,
): HeatPlanGraphViewModel {
  let table = api['response'];
  if (typeof table === 'string') {
    table = JSON.parse(table as string);
  }
  if (!table || typeof table !== 'object') {
    throw new Error('Invalid heat plan response');
  }

  const res = table as {
    columns: string[];
    data: unknown[][];
    index?: number[];
  };

  if (!res.columns?.length || !res.data?.length) {
    throw new Error('No heat plan table in response');
  }

  const dateCol = res.columns.find((c) => /^date$/i.test(String(c).trim()));

  let indexes: number[] = [];
  if (res.index?.length === res.data.length) {
    indexes = res.index.map((ts) => Number(ts));
  } else if (dateCol) {
    const dateColIdx = res.columns.indexOf(dateCol);
    indexes = res.data.map((cells) => parseDateCell(cells[dateColIdx]) ?? 0);
  } else {
    indexes = res.data.map((_, i) => Date.now() + i * 86400000);
  }

  const sortOrder = indexes
    .map((ts, i) => ({ ts, i }))
    .filter((x) => x.ts > 0)
    .sort((a, b) => a.ts - b.ts)
    .map((x) => x.i);

  const ordered =
    sortOrder.length > 0
      ? sortOrder
      : indexes.map((_, i) => i);

  indexes = ordered.map((i) => indexes[i]);
  let sortedData = ordered.map((i) => res.data[i]);

  const deduped = dedupeRowsByDate(indexes, sortedData);
  indexes = deduped.indexes;
  sortedData = deduped.rows;

  let labels = indexes.map((ts) => formatDateLabel(ts));

  let rowData = sortedData.map((cells) => {
    const obj: Record<string, unknown> = {};
    res.columns.forEach((col, i) => {
      obj[col] = cells[i] ?? '';
    });
    return obj;
  });

  const barTankOrder = discoverBarTankOrder(res.columns, rowData);
  const tankSeries: HeatPlanTankSeries[] = [];
  barTankOrder.forEach((tankName) => {
    const col = res.columns.find(
      (c) =>
        c.toUpperCase().includes(tankName) &&
        isTankTempColumn(c, rowData),
    );
    if (!col) {
      return;
    }
    const data = rowData.map((r) => {
      const n = toNumber(r[col]);
      return n !== null && isCargoTemp(n) ? n : 0;
    });
    if (data.some((v) => v > 0)) {
      tankSeries.push({
        label: tankName,
        data,
        color: TANK_PORT_COLORS[tankName] ?? '#5b8fd9',
      });
    }
  });

  const weightedCol = res.columns.find((c) => /weighted/i.test(c));
  let loadTempLine: number[] = [];
  let hasLoadTemp = false;

  if (weightedCol) {
    loadTempLine = rowData.map((r) => {
      const n = toNumber(r[weightedCol]);
      return n !== null ? n : 0;
    });
    hasLoadTemp = loadTempLine.some((v) => v > 0);
  } else {
    const loadTempMap = extractObject(api['load_temp']);
    const loadVals = Object.values(loadTempMap)
      .map((v) => toNumber(v))
      .filter((n): n is number => n !== null);
    if (loadVals.length) {
      const lt = avg(loadVals);
      loadTempLine = labels.map(() => lt);
      hasLoadTemp = true;
    } else if (tankSeries.length) {
      loadTempLine = labels.map((_, i) => {
        const vals = tankSeries.map((t) => t.data[i]).filter((v) => v > 0);
        return vals.length ? avg(vals) : 0;
      });
      hasLoadTemp = loadTempLine.some((v) => v > 0);
    }
  }

  const voyMinMap = extractObject(api['voy_min']);
  const voyMaxMap = extractObject(api['voy_max']);
  const voyMins = Object.values(voyMinMap)
    .map((v) => toNumber(v))
    .filter((n): n is number => n !== null);
  const voyMaxs = Object.values(voyMaxMap)
    .map((v) => toNumber(v))
    .filter((n): n is number => n !== null);

  const hasVoyRange = voyMins.length > 0 && voyMaxs.length > 0;
  const avgVoyMin = hasVoyRange ? avg(voyMins) : 0;
  const avgVoyMax = hasVoyRange ? avg(voyMaxs) : 0;

  let voyRangeMinLine = labels.map(() => avgVoyMin);
  let voyRangeMaxLine = labels.map(() => avgVoyMax);
  let voyRangeLabels = labels.map(() =>
    hasVoyRange ? `${Math.round(avgVoyMin)}-${Math.round(avgVoyMax)}` : '',
  );

  const sliced = sliceViewToLastDays(
    labels,
    rowData,
    indexes,
    tankSeries,
    loadTempLine,
    voyRangeMinLine,
    voyRangeMaxLine,
    voyRangeLabels,
    LAST_N_DAYS,
  );
  labels = sliced.labels;
  rowData = sliced.rowData;
  indexes = sliced.indexes;
  const chartLabels = buildChartLabels(indexes);
  const finalTanks = sliced.tankSeries;
  loadTempLine = sliced.loadTempLine;
  voyRangeMinLine = sliced.voyRangeMinLine;
  voyRangeMaxLine = sliced.voyRangeMaxLine;
  voyRangeLabels = sliced.voyRangeLabels;

  const allTemps = finalTanks.flatMap((t) => t.data).filter((v) => v > 0);
  const loadTempsPositive = loadTempLine.filter((v) => v > 0);

  const heatingStart = chartLabels[0];
  const heatingEnd = chartLabels[chartLabels.length - 1];
  const year = indexes.length
    ? new Date(indexes[indexes.length - 1]).getFullYear()
    : '';
  const heatingPeriod =
    heatingStart && heatingEnd
      ? `${heatingStart} – ${heatingEnd} ${year}`.trim()
      : undefined;

  const cargoType = api['cargo_type_name'] ?? api['cargo_type'];
  const cargoTitle = cargoType
    ? `${String(cargoType).toUpperCase()} — HEATING OVERVIEW`
    : 'HEATING TEMPERATURE OVERVIEW';

  const loadPort =
    readPortField(api, ['load_port', 'load_ports', 'start_port', 'loadPort']) ??
    row.load_port;
  const dischargePort =
    readPortField(api, [
      'discharge_port',
      'disch_port',
      'disch_ports',
      'destination_port',
      'dischargePort',
    ]) ?? row.discharge_port;

  const heatPlanNumber = String(
    api['hp_number'] ?? api['heatplan_no'] ?? row.hp_np ?? '',
  ).trim() || undefined;

  const kpis: HeatPlanGraphKpis = {};
  if (loadTempsPositive.length) {
    kpis.avgLoadTemp = avg(loadTempsPositive);
  }
  if (hasVoyRange) {
    kpis.avgVoyMin = avgVoyMin;
    kpis.avgVoyMax = avgVoyMax;
  }
  if (allTemps.length) {
    kpis.maxHeatingTemp = Math.max(...allTemps);
    kpis.minHeatingTemp = Math.min(...allTemps);
  }

  const tableColumns = pickTableColumns(res.columns, rowData);

  const partial = {
    heatPlanNumber,
    loadPort,
    dischargePort,
    heatingPeriod,
  };

  return {
    vesselName: String(api['vessel_name'] ?? row.ship_name ?? 'Vessel'),
    vesselImo: String(api['imo'] ?? row.ship_imo ?? ''),
    ...partial,
    cargoTitle,
    labels,
    chartLabels,
    tableColumns,
    rowData,
    tankSeries: finalTanks,
    loadTempLine,
    voyRangeMinLine,
    voyRangeMaxLine,
    voyRangeLabels,
    hasLoadTemp,
    hasVoyRange,
    kpis,
    infoFields: buildInfoFields(partial),
  };
}

export function buildComparisonMetrics(
  original: HeatPlanGraphViewModel,
  uploaded: HeatPlanGraphViewModel,
): HeatPlanComparisonRow[] {
  const rows: HeatPlanComparisonRow[] = [];

  const addRow = (
    parameter: string,
    orig: number | undefined,
    up: number | undefined,
    format: (n: number) => string = (n) => n.toFixed(2),
  ) => {
    if (orig === undefined || up === undefined) {
      return;
    }
    const diff = up - orig;
    rows.push({
      parameter,
      originalDisplay: format(orig),
      uploadedDisplay: format(up),
      difference: diff,
      trend: diff > 0.01 ? 'up' : diff < -0.01 ? 'down' : 'flat',
    });
  };

  addRow('AVG Load Temp', original.kpis.avgLoadTemp, uploaded.kpis.avgLoadTemp);
  addRow(
    'AVG Voy Range Min',
    original.kpis.avgVoyMin,
    uploaded.kpis.avgVoyMin,
    (n) => String(Math.round(n)),
  );
  addRow(
    'AVG Voy Range Max',
    original.kpis.avgVoyMax,
    uploaded.kpis.avgVoyMax,
    (n) => String(Math.round(n)),
  );
  addRow(
    'Max Heating Temp',
    original.kpis.maxHeatingTemp,
    uploaded.kpis.maxHeatingTemp,
    (n) => n.toFixed(1),
  );
  addRow(
    'Min Heating Temp',
    original.kpis.minHeatingTemp,
    uploaded.kpis.minHeatingTemp,
    (n) => n.toFixed(1),
  );

  return rows;
}

export function parseHeatPlanTableToView(
  table: HeatPlanExcelTable,
  row: HeatPlanGraphRowMeta = {},
  vesselLabel = 'Uploaded Plan',
): HeatPlanGraphViewModel {
  const loadTemp: Record<string, number> = {};
  const voyMin: Record<string, number> = {};
  const voyMax: Record<string, number> = {};

  if (table.footer) {
    Object.assign(loadTemp, table.footer.loadTempByTank);
    Object.assign(voyMin, table.footer.voyMinByTank);
    Object.assign(voyMax, table.footer.voyMaxByTank);
  }

  return parseHeatPlanApiToView(
    {
      vessel_name: vesselLabel,
      response: {
        columns: table.columns,
        index: table.index,
        data: table.data,
      },
      load_temp: loadTemp,
      voy_min: voyMin,
      voy_max: voyMax,
    },
    row,
  );
}

export async function parseHeatPlanExcelFile(
  file: File,
): Promise<HeatPlanGraphViewModel> {
  const table = await readHeatPlanExcelTable(file);
  return parseHeatPlanTableToView(table, {}, 'Uploaded Plan');
}

export {
  buildOverviewChartConfig,
  buildTankPairComparisonChart,
  buildLoadTempComparisonChart,
  buildVoyRangeComparisonChart,
  HEAT_PLAN_CHART_THEME,
} from './heat-plan-graph-charts.util';
